/**
 * @fileoverview Set-password form, shown on a valid invitation link.
 * @description Client-side validated (via React Hook Form + buildSetPasswordFormSchema) form for
 * choosing the account's first password: a live rule checklist driven by the real
 * GET /identity/password-policy response (usePasswordPolicy, rendered via PasswordPolicyHints —
 * shared with ChangePasswordForm) rather than a hardcoded copy, a confirm-password match hint,
 * and submit loading/error state. The schema is rebuilt against the same fetched policy on every
 * render, so submit is blocked with the same rules the checklist shows unmet, not just a bare
 * non-empty check. Submits via useAcceptInvitation, which continues the flow into the
 * activation-code step (/two-factor) on success.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { type FormEvent } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  PasswordMatchHint,
  PasswordPolicyChecklist,
} from '@/features/auth/components/PasswordPolicyHints'
import { useAcceptInvitation } from '@/features/auth/hooks/useAcceptInvitation'
import { usePasswordPolicy } from '@/features/auth/hooks/usePasswordPolicy'
import { buildSetPasswordFormSchema } from '@/features/auth/schemas/set-password-form.schema'
import Button from '@/shared/components/atoms/Button'
import PasswordField from '@/shared/components/molecules/PasswordField'

import type { SetPasswordFormValues } from '@/features/auth/schemas/set-password-form.schema'

interface SetPasswordFormProps {
  token: string
}

/**
 * @component
 * @param {SetPasswordFormProps} props - The invitation token being redeemed.
 * @param {string} props.token - The token from the /invitation/accept URL.
 */
function SetPasswordForm({ token }: SetPasswordFormProps) {
  const { t } = useTranslation()
  const acceptInvitationMutation = useAcceptInvitation()
  const { data: policy } = usePasswordPolicy()
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SetPasswordFormValues>({
    resolver: zodResolver(buildSetPasswordFormSchema(policy)),
    defaultValues: { newPassword: '', confirmPassword: '' },
  })

  const newPassword = useWatch({ control, name: 'newPassword' })
  const confirmPassword = useWatch({ control, name: 'confirmPassword' })

  /**
   * @description Submits the accept-invitation mutation with the schema-validated password.
   * @param {SetPasswordFormValues} values - The validated new/confirm passwords.
   */
  const onValid = (values: SetPasswordFormValues) => {
    acceptInvitationMutation.mutate({
      token,
      newPassword: values.newPassword,
      confirmPassword: values.confirmPassword,
    })
  }

  /**
   * @description Runs the schema validation and, if valid, calls onValid. Wrapped so the DOM
   * onSubmit handler returns void rather than the Promise React Hook Form's handleSubmit produces.
   * @param {FormEvent<HTMLFormElement>} event - The form submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  return (
    <form className="mt-7 flex flex-col gap-[22px]" onSubmit={onSubmit} noValidate>
      {/* Deliberately empty decoy fields, not bound to the real email: two adjacent password
          inputs alone can read as a login form to a browser's autofill heuristics, and a
          username field prefilled with this account's actual address would give it exactly the
          match it needs to offer that account's saved password onto the fields below. Left
          empty, these just absorb the browser's autofill attempt instead of the real fields. */}
      <input
        type="text"
        name="username"
        autoComplete="off"
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only"
      />
      <input
        type="password"
        name="password"
        autoComplete="off"
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only"
      />

      <div>
        <PasswordField
          id="newPassword"
          label={t('acceptInvitation.newPasswordLabel')}
          placeholder={t('acceptInvitation.newPasswordPlaceholder')}
          autoComplete="new-password"
          error={errors.newPassword && t(errors.newPassword.message ?? '')}
          {...register('newPassword', { onChange: () => acceptInvitationMutation.reset() })}
        />
        <PasswordPolicyChecklist policy={policy} value={newPassword} />
      </div>

      <div>
        <PasswordField
          id="confirmPassword"
          label={t('acceptInvitation.confirmPasswordLabel')}
          placeholder={t('acceptInvitation.confirmPasswordPlaceholder')}
          autoComplete="new-password"
          error={errors.confirmPassword && t(errors.confirmPassword.message ?? '')}
          {...register('confirmPassword', { onChange: () => acceptInvitationMutation.reset() })}
        />
        <PasswordMatchHint newPassword={newPassword} confirmPassword={confirmPassword} />
      </div>

      {acceptInvitationMutation.isError && (
        <p role="alert" className="text-[14px] text-red-600">
          {acceptInvitationMutation.error.message}
        </p>
      )}

      <Button type="submit" isLoading={acceptInvitationMutation.isPending}>
        {acceptInvitationMutation.isPending
          ? t('acceptInvitation.submitPending')
          : t('acceptInvitation.submitIdle')}
      </Button>
    </form>
  )
}

export default SetPasswordForm
